import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import {
  getActKnoInspectorBlockId,
  getActKnoInspectorsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import {
  getKnoInspectorsArrayErrorMessage,
  INSPECTORS_SECTION_DECISION_LABEL,
  INSPECTORS_SECTION_KNM_LABEL,
  INSPECTORS_SECTION_PM_LABEL,
  KNO_INSPECTORS_DECISION_LABEL,
  KNO_INSPECTORS_KNM_LABEL,
  KNO_INSPECTORS_PM_LABEL,
  makeKnoInspectorsInPassportValidator,
} from 'KnmErknmPackage/MainModule/common/getSignerInInspectorsError';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import styles from './KnmActParticipants.module.scss';
import { useShowMore } from '../../../hooks/useShowMore';
import { Spinner } from '../../../UIPackage/MainModule/components/Spinner';

interface IErknmActInspectorsProps {
  id: string;
  index?: number;
  inspectorsErknm: IKnmErknmFormInspector[] | undefined;
  name: string;
  isDisabledField: boolean;
  isDecision?: boolean;
  isPm?: boolean;
  isKnm?: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

export const KnmActInspectors = ({
  id,
  index,
  inspectorsErknm,
  name,
  formRef,
  isDisabledField,
  isDecision,
  isPm,
  isKnm,
}: IErknmActInspectorsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  // @ts-ignore
  const knoInspectors = formRef?.current.getFieldState(`${name}.knoInspectors`)?.value as IKnmErknmFormInspector[];
  const knoInspectorsBlockId = getActKnoInspectorsBlockId(id);

  const addField = (fieldName: string) => () =>
    !isDisabledField && push && push(`${name}.${fieldName}`, { listId: uuidv4(), isNew: true });

  const makeRemoveField = (fieldName: string, i: number) => () => {
    if (isDisabledField || !remove) {
      return;
    }

    remove(`${name}.${fieldName}`, i);
  };

  /**
   * Фильтрованный список вариантов для выбора должностного лица, без ранее использованных + с заполненной информацией об инспекторе
   */
  const inspectorsErknmOptionsFiltered = useMemo(() => {
    return (
      inspectorsErknm
        ?.filter(
          inspector =>
            inspector.fullName &&
            inspector.position &&
            (Array.isArray(knoInspectors)
              ? !knoInspectors?.find(
                  i =>
                    i.knoInspector?.value.listId === inspector.listId ||
                    i.knoInspector?.value.fullName === inspector.fullName,
                )
              : true),
        )
        .map(inspector => {
          return {
            id: inspector.guid || '',
            value: inspector,
            label: `${inspector.fullName} - ${inspector.position?.label}`,
          };
        }) || []
    );
  }, [inspectorsErknm, knoInspectors]);

  const groupLabel = useMemo(() => {
    if (isDecision) {
      return KNO_INSPECTORS_DECISION_LABEL;
    }
    if (isPm) {
      return KNO_INSPECTORS_PM_LABEL;
    }

    return KNO_INSPECTORS_KNM_LABEL;
  }, [isDecision, isPm]);

  /**
   * Валидатор списка ДЛ акта: каждое ДЛ должно присутствовать среди должностных лиц паспорта.
   * Не проверяем, если в акте заполнен firstExtractDateTime.
   * Проверку даты — внутри функции: FieldArray захватывает validate через useConstant и не обновляет проп.
   */
  const validateKnoInspectors = useMemo(() => {
    let inspectorsSectionLabel = INSPECTORS_SECTION_KNM_LABEL;

    if (isDecision) {
      inspectorsSectionLabel = INSPECTORS_SECTION_DECISION_LABEL;
    }

    if (isPm) {
      inspectorsSectionLabel = INSPECTORS_SECTION_PM_LABEL;
    }

    const passportInspectorsFieldName = isDecision ? 'inspectors' : 'inspectorsErknm';
    const inPassportValidator = makeKnoInspectorsInPassportValidator(
      passportInspectorsFieldName,
      inspectorsSectionLabel,
    );

    return (actKnoInspectors: IKnmErknmFormInspector[] | undefined, allValues: object) => {
      if (getIn(allValues, `${name}.firstExtractDateTime`)) {
        return undefined;
      }

      return inPassportValidator(actKnoInspectors, allValues);
    };
  }, [isDecision, isPm, name]);

  return (
    <FieldBlock
      id={knoInspectorsBlockId}
      className={styles.GroupMarginBottom}
      label={getTitleFieldWithRedStar({
        label: groupLabel,
        field: `${name}.knoInspectors`,
        isPm,
        isKnm,
        isDecision,
      })}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={isDisabledField}
          onClick={addField('knoInspectors')}
        />
      }
    >
      <FieldArray name={`${name}.knoInspectors`} validate={validateKnoInspectors}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormInspector, HTMLElement>) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 10,
          });

          if (!fields.length) {
            return null;
          }

          return (
            <>
              <Row>
                {visibleItems.map((fieldValue: IKnmErknmFormInspector, i: number) => {
                  // visibleItems — префикс fields.value, индекс совпадает.
                  // Не ищем по listId: у строк без listId findIndex всегда даёт 0 → дубли ключей/полей.
                  const rowListId = fieldValue.listId || `idx-${i}`;
                  const fieldName = `${name}.knoInspectors[${i}]`;
                  const knoInspectorId = `${getActKnoInspectorBlockId(id, rowListId)}.knoInspector`;

                  return (
                    <Row key={knoInspectorId} margin="medium" isListItem notSpace>
                      <SelectInputField
                        className={styles.SelectInputKnoInspectors}
                        name={`${fieldName}.knoInspector`}
                        id={knoInspectorId}
                        placeholder={getTitleFieldWithRedStar({
                          label: 'Выберите должностное лицо',
                          field: `${fieldName}.knoInspector`,
                          isPm,
                          isKnm,
                          isDecision,
                        })}
                        options={inspectorsErknmOptionsFiltered}
                        isSearchable
                        disabled={isDisabledField}
                        size="medium"
                      />
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveField('knoInspectors', i)} />
                    </Row>
                  );
                })}
              </Row>
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
            </>
          );
        }}
      </FieldArray>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (!isDecision && index !== undefined && props.errors?.acts && props.errors?.acts[index]?.knoInspectors) {
            const errorMessage = getKnoInspectorsArrayErrorMessage(props.errors.acts[index].knoInspectors);
            const isFieldTouched = props.touched?.[`acts[${index}].knoInspectors`];

            if (isFieldTouched && errorMessage) {
              return <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>{errorMessage}</p>;
            }
          }
          if (!isDecision && index === undefined && props.errors?.act && props.errors?.act?.knoInspectors) {
            const errorMessage = getKnoInspectorsArrayErrorMessage(props.errors.act.knoInspectors);
            const isFieldTouched = props.touched?.[`act.knoInspectors`];

            if (isFieldTouched && errorMessage) {
              return <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>{errorMessage}</p>;
            }
          }
          if (isDecision && props.errors?.organization?.act && props.errors?.organization?.act.knoInspectors) {
            const errorMessage = getKnoInspectorsArrayErrorMessage(props.errors.organization.act.knoInspectors);
            const isFieldTouched = props.touched?.[`organization.act.knoInspectors`];

            if (isFieldTouched && errorMessage) {
              return <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>{errorMessage}</p>;
            }
          }

          return null;
        }}
      </FormSpy>
    </FieldBlock>
  );
};
